import {globalStores, ui} from '@stores'
import {EsSearchResponse, MESSearchResponses} from '@stores/elasticsearch/ElasticSearchStore'
import {socketIO} from '@stores/socketIO'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {authStore, guid, removeUndefinedsNested} from '@utils'
import axios, {AxiosError, AxiosInstance, CancelToken} from 'axios'
import {configs} from '@stores/config'
import {autorun, runInAction, _} from '@api'
import {computed, observable, toJS, trace} from 'mobx'
import {utils} from '@modules'

const SHOW_WHY_NO_BATCH_QUERY = false

/**
 * Debug info we can include in our request headers.  Must be added to elasticsearch config or will cause CORS errors:
 *
 */
const ES_META_HEADER = 'X-Meta'

export type ElasticSearchError = Error & {
  extraJson?,
  statusCode?: number
}

const wrapAxiosError = (url, options, err: AxiosError<any> & ElasticSearchError, meta?: any) => {
  console.error(err)
  let toThrow: ElasticSearchError = err

  if (err?.response?.status == 401 || err?.response?.data?.error?.type === 'security_exception') {
    //console.info(`Current ES token is invalid, logging out...`, err.response.data.error.reason)
    authStore.logout()
    //return new Error(err.response.data.error.reason)
  } else if (err?.response?.status == 404) {
    toThrow = new Error(`Not found (404):  ${url}`)
  } else {
    if (axios.isCancel(err)) {
      throw err
    }

    toThrow = new Error(!DEBUG ? 'An error has occurred querying the server.'
                               : `${meta ? `${meta}\n` : ''}Error querying ElasticSearch:\n${!err ? 'No Information' : err?.response?.data?.error ? JSON.stringify(err.response.data.error, null, 2) : err}`) as any
    //debugger
  }

  if (DEBUG) {
    toThrow.extraJson = [options && {label: 'Request:', json: options}, {label: 'Response', json: err?.response?.data?.error}].filter(_.identity)
  }

  toThrow.statusCode = err?.response?.status ?? err?.statusCode

  return toThrow
}

export class ElasticSearchClient implements IElasticSearchClient {
  /**
   * GraphQL Endpoint
   */
  @observable.ref axiosInstance: AxiosInstance = null

  /**
   * /services endpoint
   */
  @observable.ref axiosServicesInstance: AxiosInstance = null

  /**
   * /nest endpoint
   */
  @observable.ref axiosNest: AxiosInstance = null

  /**
   * Testing ElasticSearch directly
   */
  @observable.ref axiosEs: AxiosInstance = null

  private _batch: EsBatching.SearchBatchingStore

  constructor() {
    this._batch = new EsBatching.SearchBatchingStore(this)

    synchronizeStores(() => {
      autorun(() => {
        const {config: {graphqlEndpoint, esApiKey, servicesUrl, secondaryServicesUrl, esUrl}, config} = configs,
          {accessToken} = authStore,
          {settings: {services: {nest: shouldUseNest}}} = ui,
          {userSettings} = globalStores,
            dev = toJS(userSettings.dev),
          servicesEndpointUrl = servicesUrl ?? config['servicesEndpoint']

        // trace()
        runInAction(() => {
          if (this.axiosInstance?.defaults?.baseURL !== graphqlEndpoint) {
            this.axiosInstance = axios.create({
              baseURL: graphqlEndpoint
            })
          }
          this.axiosInstance.defaults.headers.common['Authorization'] = accessToken

          if (this.axiosServicesInstance?.defaults?.baseURL !== servicesEndpointUrl) {
            this.axiosServicesInstance = axios.create({
              baseURL: servicesEndpointUrl
            })
          }
          this.axiosServicesInstance.defaults.headers.common['Authorization'] = accessToken

          if (esUrl) {
            if (!esApiKey) {
              console.warn(`ElasticSearch URL specified but no API key`)
            } else {
              if (this.axiosEs?.defaults?.baseURL !== esUrl) {
                this.axiosEs = axios.create({
                  baseURL: `${esUrl}`
                })
              }
              this.axiosEs.defaults.headers.common['Authorization'] = `ApiKey ${esApiKey}`
            }
          }

          if (shouldUseNest || secondaryServicesUrl || dev?.localNest) {
            const nestEndpoint = DEBUG && dev?.localNest ? 'http://localhost:5959' : (secondaryServicesUrl ?? `${servicesEndpointUrl.substring(0, _.trim(servicesEndpointUrl, '/').nthLastIndexOf('/', 1))}/design-board-nest/`)

            if (this.axiosNest?.defaults?.baseURL !== nestEndpoint) {
              this.axiosNest = axios.create({
                baseURL: nestEndpoint
              })
            }
            this.axiosNest.defaults.headers.common['Authorization'] = accessToken
            this.axiosNest.defaults.headers.common['x-webclient-id'] = socketIO.clientId
          }
        })
        // if (!esAuthToken && authStore.userIsAuthenticated) {
        //   console.debug(`Logging out due to no ES token...`)
        //   nav.routeToLogoutPage()
        // }
      }, {name: `Create axios instance and set auth header`})
    })
  }

  @computed get url() {
    return _.trim(configs.config.graphqlEndpoint ?? `${window.location.protocol}${window.location.host}/design-board-graphql`, '/')
  }

  private _esUrlFor = (slug: string) => {
    const {axiosEs} = this

    if (axiosEs) {
      if (slug === 'msearch' || slug === 'search') return `_${slug}`

      return slug
    }

    return `/es/${slug}`

  }

  /**
   * To search with multiple queries
   * @param esOptions
   */
  msearch = async (esOptions: IEsMSearchOptions): Promise<Partial<MESSearchResponses>> => {
    let url = this.url
    const options = esOptions
    try {
      let data
      const {axiosEs} = this,
        axiosOptions = {
          ..._.pick(esOptions, ['cancelToken']),
          headers: {
            'Content-Type': 'application/x-ndjson',
            ['X-Meta']: JSON.stringify(esOptions.meta?.replaceAll?.('→', '->'))}
        }

      if (axiosEs) {
        // Elastic Cloud has very specific requirements to support msearch
        // https://www.elastic.co/guide/en/elasticsearch/reference/7.16/search-multi-search.html

        const payload = esOptions.body.reduce((acc, v) => acc + JSON.stringify(v, null, 0) + '\n', '')
        data = await axiosEs.post('_msearch', payload, axiosOptions)
      } else {
        data = await this.client.post(
          this._esUrlFor('msearch'), {
            meta: `msearch for ${options.body.length / 2} queries`,
            ...options
          }, axiosOptions)
      }

      if (data?.data?.response?.status === 404) {
        // The index doesn't exist
        console.warn(`Unable to find index '${esOptions.index}'`)
        return {responses: []}
      }
      if (data?.data?.response?.status === 400 || data?.data?.response?.status === 500) {
        throw new Error(`Error msearching accelerator:  ${JSON.stringify(data.data, null, 2)}`)
      }
      return data.data
    } catch (err) {
      throw wrapAxiosError(url, options, err)
    }
  }

  @computed get client() {
    const {axiosEs, axiosNest, axiosServicesInstance} = this
    return axiosEs ?? axiosNest ?? axiosServicesInstance
  }

  /**
   * To search index with options from elasticsearch
   * @param esOptions
   */
  search = async (esOptions: IEsSearchOptions): Promise<Partial<EsSearchResponse>> => {
    let url = this.url
    const options = _.omit(esOptions, ['cancelToken']),
      {_batch} = this

    try {
      if (esOptions.batch !== false && _batch.queueNotEmpty) {
        if (esOptions.cancelToken) {
          SHOW_WHY_NO_BATCH_QUERY && DEBUG && console.debug(`Can't batch query due to cancellation token`, {esOptions})
        } else if (utils.enlist(esOptions.body.query?.bool?.should)?.some(v => v?.term?._index)) {
          SHOW_WHY_NO_BATCH_QUERY && DEBUG && console.debug(`Can't batch query due to individual index should terms (msearch is 1-1 search-index)`, {esOptions})
        } else {
          return await this._batch.search(esOptions)
        }
      }

      const {axiosEs} = this,
        axiosOptions = {
          ..._.pick(esOptions, ['cancelToken']),
          headers: {['X-Meta']: esOptions.meta?.replaceAll?.('→', '->')}
        },
        data = await (
          axiosEs ? this.client.post(
                    `${options.index ? `${options.index}/` : ''}_search`,
                    options.body,
                    axiosOptions
                  )
                  : this.client.post(
                    this._esUrlFor(`search`),
                    options,
                    axiosOptions
                  ))

      if (data?.data?.response?.status === 404) {
        // The index doesn't exist
        console.warn(`Unable to find index '${esOptions.index}'`)
        return {hits: {hits: [], total: {relation: 'eq', value: 0}}}
      }

      if (data?.data?.response?.status === 400 || data?.data?.response?.status === 500 || data?.data?.response?.status === 429) {
        throw new Error(`${esOptions.meta ? `${esOptions.meta}\n` : ''}Error searching accelerator:  ${JSON.stringify(data.data, null, 2)}`)
      }
      return data.data
    } catch (err) {
      throw wrapAxiosError(url, options, err)
    }
  }

  /**
   * Get index
   * @param getOptions
   */
  get = async ({index, id, _source, ignoreError, meta}: IGetOptions) => {
    if (!this.axiosServicesInstance) {
      return null
    }

    if (!index) {
      debugger
    }

    let url = this.url
    try {
      const {axiosEs} = this,
        slug = `${encodeURIComponent(id)}?${_source ? `_source=${encodeURI(_source.join(','))}` : ''}`

      const getString = this._esUrlFor(axiosEs ? `${index}/_doc/${slug}` : `get/${index}/${slug}`)
      const data = await this.client.get(getString, removeUndefinedsNested({headers: {[ES_META_HEADER]: meta?.replaceAll?.('→', '->')}}))

      // console.trace(getString)

      if ((data?.data?.meta?.statusCode === 404 || data?.data?.meta?.statusCode === 500) && !ignoreError) {
        const err = new Error(`Error getting a document:  ${JSON.stringify(data.data, null, 2)}`) as ElasticSearchError
        err.statusCode = data?.data?.meta?.statusCode
        throw err
      }

      const r = data.data?._source

      return r
    } catch (err) {
      throw wrapAxiosError(url, null, err, meta)
    }
  }

  /**
   * Partial update of document
   */
  updateByQuery = async ({index, id, doc}: IUpdateDocOptions) => {
    if (!this.axiosNest) {
      return null
    }

    if (!id) {
      throw new Error(`id is required`)
    }

    let url = this.url
    try {
      const data = await this.axiosNest.post(`/es/update/${index}/${encodeURIComponent(id)}`, {doc})

      if (data?.data?.meta?.statusCode === 404) {
        throw new Error(`Error updating a document:  ${JSON.stringify(data.data, null, 2)}`)
      }
      if (data?.data?.response?.status === 400 || data?.data?.response?.status === 500) {
        throw new Error(`Error searching accelerator:  ${JSON.stringify(data.data, null, 2)}`)
      }

      return data.data?._source
    } catch (err) {
      throw wrapAxiosError(url, null, err)
    }
  }
}

export interface IElasticSearchClient {
  search(options: IEsSearchOptions)

  get(options: IGetOptions)

  updateByQuery(options: IUpdateDocOptions)
}

export interface IEsMSearchOptions {
  body: Array<{ index: string } | EsSearchQueryBody>
  index?: string
  meta?: string
  cancelToken?: CancelToken
  batch?: boolean
}

export type EsSearchQueryBody = any

export interface IEsSearchOptions {
  body: EsSearchQueryBody
  index?: string
  meta?: any
  cancelToken?: CancelToken
  batch?: boolean
}

export interface IGetOptions {
  index: string
  id: string
  meta?: any
  _source?: Array<string>
  ignoreError?: boolean
}

export interface IUpdateDocOptions {
  index: string
  meta?: any
  id: string
  doc: any
}

namespace EsBatching {
  type EsSearchBatchingOptions<T = any> = {
    queryId: string,
    esOptions: IEsSearchOptions
    resolve: (value: T | PromiseLike<T>) => void
    reject: (reason?: any) => void
  }

  export class SearchBatchingStore {
    @computed get queueNotEmpty() {
      return this.searchBatchQueue.length < ui.settings.es?.maxBatchQueue ?? 5
    }
    constructor(public client: ElasticSearchClient) {

    }

    searchBatchQueue: Array<EsSearchBatchingOptions> = []

    searchDebounced = _.debounce(async () => {
      runInAction(async () => {
        const {searchBatchQueue, client} = this,
          valuesToBatch = searchBatchQueue

        this.searchBatchQueue = []

        try {
          if (valuesToBatch.length === 1) {
            const {esOptions, resolve, reject} = valuesToBatch[0]
            try {
              const r = await this.client.search({...esOptions, batch: false})
              resolve(r)
            } catch (err) {
              reject(err)
            }
          } else {
            // Create an msearch query that combines all of these queries
            const queryBody = _.flatMap(valuesToBatch.map(({esOptions: {index, body}}) => {
              return [
                {index},
                body
              ]
            }))

            //_.partition(valuesToBatch, MAX
            const {responses} = await client.msearch({
              meta: JSON.stringify(valuesToBatch.reduce((acc, q, i) => _.assign(acc, {[i]: q.esOptions.meta?.replace?.('→', '->')}), {})),
              body: queryBody
              //cancelToken
            })

            //logJS({responses, valuesToBatch})
            runInAction(() => {
              responses.forEach((v, i) => {
                const {queryId, esOptions, resolve} = valuesToBatch[i]
                resolve(v)
              })
            })
          }
        } catch (err) {
          console.error(err)
          valuesToBatch.forEach(({reject}) => {
            reject(err.message)
          })
        }
      })
    }, ui?.settings?.batch?.ms)

    search = async (esOptions: IEsSearchOptions): Promise<Partial<EsSearchResponse>> => {
      return new Promise((resolve, reject) => {
        const queryId = guid(), {searchBatchQueue} = this
        searchBatchQueue.push({queryId, esOptions, resolve, reject})
        this.searchDebounced()
      })
    }
  }
}

export const esClient = new ElasticSearchClient()


// WEBPACK FOOTER //
// ./src/stores/elasticsearch/ElasticSearchClient.ts